import styled from 'styled-components';

import { media } from 'src/styles/breakpoints';

export const ModalTitle = styled.h2`
  margin: 0 0 1rem;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.dark};
`;

export const KycTabs = styled.nav`
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin: 0 0 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.grey};
`;

export const KycTab = styled.button<{ $active: boolean }>`
  position: relative;
  padding: 0.75rem 0;
  border: none;
  background: transparent;
  color: ${({ theme, $active }) => ($active ? theme.dark : theme.midGrey)};
  font-size: 1rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 3px;
    background: ${({ theme, $active }) => ($active ? theme.primary : 'transparent')};
  }
`;

export const FieldList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.grey};
  border-radius: 0.75rem;
  overflow: hidden;
  background: ${({ theme }) => theme.background};
`;

export const FieldRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.grey};

  &:last-child {
    border-bottom: none;
  }
`;

export const FieldBody = styled.div`
  min-width: 0;
  flex: 1;
`;

export const FieldLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 400;
  color: ${({ theme }) => theme.midGrey};
  margin-bottom: 0.25rem;
`;

export const FieldValue = styled.div<{ $emphasis?: boolean }>`
  font-size: 0.9375rem;
  font-weight: ${({ $emphasis }) => ($emphasis ? 500 : 400)};
  color: ${({ theme }) => theme.dark};
  word-break: break-word;
`;

export const CopyButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  border: none;
  border-radius: 0.375rem;
  background: transparent;
  color: ${({ theme }) => theme.midGrey};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.dark};
    background: ${({ theme }) => theme.lightGrey};
  }
`;

export const ImagesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-top: 0.35rem;

  ${media.md} {
    grid-template-columns: 1fr;
  }
`;

export const ImageSlot = styled.div<{ $tall?: boolean }>`
  position: relative;
  width: ${({ $tall }) => ($tall ? 'min(12rem, 100%)' : '100%')};
  height: ${({ $tall }) => ($tall ? '16rem' : '10.5rem')};
  margin-top: 0.35rem;
  border-radius: 0.5rem;
  border: 1px solid ${({ theme }) => theme.grey};
  background: ${({ theme }) => theme.lightGrey};
  overflow: hidden;
`;

export const ImageSlotImg = styled.img<{ $ready: boolean }>`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  opacity: ${({ $ready }) => ($ready ? 1 : 0)};
  transition: opacity 0.15s ease;
`;

export const ImageSlotStatus = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.midGrey};
  font-size: 0.8125rem;
  pointer-events: none;
`;

export const DocPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 8rem;
  margin-top: 0.35rem;
  border-radius: 0.5rem;
  border: 1px dashed ${({ theme }) => theme.grey};
  background: ${({ theme }) => theme.lightGrey};
  color: ${({ theme }) => theme.midGrey};
  font-size: 0.8125rem;
`;

export const TabPanel = styled.div<{ $hidden?: boolean }>`
  display: ${({ $hidden }) => ($hidden ? 'none' : 'block')};
`;

export const FeedbackBox = styled.div`
  margin-top: 1rem;
  padding: 0.85rem 1rem;
  border: 1px solid ${({ theme }) => theme.grey};
  border-radius: 0.75rem;
  background: ${({ theme }) => theme.background};
`;

export const FeedbackInput = styled.textarea`
  width: 100%;
  min-height: 3.5rem;
  margin: 0;
  padding: 0;
  border: none;
  outline: none;
  resize: vertical;
  font: inherit;
  font-size: 0.9375rem;
  font-weight: 400;
  color: ${({ theme }) => theme.dark};
  background: transparent;

  &::placeholder {
    color: ${({ theme }) => theme.midGrey};
  }

  &:disabled {
    color: ${({ theme }) => theme.dark};
    cursor: default;
  }
`;

/**
 * Sized for however many actions the record allows — Approve, Reject and
 * Re-verify each drop out on their own, so a fixed column count leaves the
 * survivors stranded at half width.
 */
export const ActionRow = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  gap: 0.75rem;
  margin-top: 1.25rem;

  ${media.md} {
    grid-auto-flow: row;
  }
`;

export const SliderOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4.5rem 5.5rem;
  background: ${({ theme }) => theme.modalBackground ?? 'rgba(15, 18, 28, 0.55)'};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);

  ${media.md} {
    padding: 3.5rem 1rem;
  }
`;

export const SliderShell = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: min(42rem, 100%);
`;

export const SliderCard = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.background};
  border-radius: 1.25rem;
  padding: 1.25rem 1.5rem 1.5rem;
  box-shadow:
    0 24px 48px -12px rgba(15, 18, 28, 0.35),
    0 12px 24px -8px rgba(15, 18, 28, 0.2);
`;

export const SliderHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

export const SliderTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.dark};
`;

export const SliderClose = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  color: ${({ theme }) => theme.midGrey};
  font-size: 1.35rem;
  line-height: 1;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.dark};
    background: ${({ theme }) => theme.lightGrey};
  }
`;

export const SliderFrame = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 18rem;
  padding: 1rem;
  border-radius: 0.875rem;
  background: ${({ theme }) => theme.lightGrey};
`;

export const SliderImage = styled.img`
  display: block;
  max-width: 100%;
  max-height: min(60vh, 28rem);
  object-fit: contain;
  border-radius: 0.5rem;
`;

export const SliderNav = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${({ $side }) => ($side === 'left' ? 'left: -3.75rem;' : 'right: -3.75rem;')}
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #fff;
  color: #111;
  box-shadow: 0 8px 20px rgba(15, 18, 28, 0.2);
  cursor: pointer;

  svg {
    width: 1.35rem;
    height: 1.35rem;
    color: #111;
    fill: #111;
    stroke: #111;
    flex-shrink: 0;
  }

  &:hover {
    filter: brightness(0.97);
  }

  ${media.md} {
    ${({ $side }) => ($side === 'left' ? 'left: 0.35rem;' : 'right: 0.35rem;')}
  }
`;

/* ---------------------------------------------------------------------------
 * KYC list screen
 * ------------------------------------------------------------------------- */

export const PageTabs = styled.nav`
  display: flex;
  flex-wrap: nowrap;
  gap: 1.5rem;
  margin: 0 0 1.25rem;
  border-bottom: 1px solid ${({ theme }) => theme.grey};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const PageTab = styled.button<{ $active: boolean }>`
  flex-shrink: 0;
  position: relative;
  padding: 0.75rem 0;
  border: none;
  background: transparent;
  color: ${({ theme, $active }) => ($active ? theme.dark : theme.midGrey)};
  font-size: 1.0625rem;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s ease;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 2px;
    border-radius: 2px 2px 0 0;
    background: ${({ theme, $active }) => ($active ? theme.dark : 'transparent')};
  }

  &:hover {
    color: ${({ theme }) => theme.dark};
    opacity: 0.85;
  }
`;

export const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
`;

export const StatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  padding: 10px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  background: ${({ $status, theme }) => {
    const s = $status.toLowerCase().replace(/_/g, ' ');
    if (['verified', 'approved', 'yes', 'veriff'].includes(s)) return theme.positive90;
    if (['canceled', 'cancelled', 'rejected', 'declined', 'failed', 'abandoned'].includes(s)) {
      return theme.negative90;
    }
    if (
      [
        'pending',
        'submitted',
        'review',
        'needs review',
        'resubmission requested',
        'expired',
        'started',
      ].includes(s)
    ) {
      return theme.middle90;
    }
    return theme.lightGrey;
  }};
  color: ${({ $status, theme }) => {
    const s = $status.toLowerCase().replace(/_/g, ' ');
    if (['verified', 'approved', 'yes', 'veriff'].includes(s)) return theme.positive;
    if (['canceled', 'cancelled', 'rejected', 'declined', 'failed', 'abandoned'].includes(s)) {
      return theme.negative;
    }
    if (
      [
        'pending',
        'submitted',
        'review',
        'needs review',
        'resubmission requested',
        'expired',
        'started',
      ].includes(s)
    ) {
      return theme.middle;
    }
    return theme.midGrey;
  }};
`;

export const UserLink = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  color: ${({ theme }) => theme.primary};
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  /* Long emails have no break opportunity of their own and would otherwise
     run past the card edge. */
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;

  &:hover {
    text-decoration: underline;
  }
`;

/* ---------------------------------------------------------------------------
 * KYC detail modal
 * ------------------------------------------------------------------------- */

export const Breadcrumb = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ theme }) => theme.midGrey};
  margin-bottom: 0.75rem;
`;

export const HeadlineRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

export const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
`;

export const Title = styled.h2`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.625rem;
  margin: 0;
  font-size: 1.375rem;
  font-weight: 700;
  line-height: 1.3;
  color: ${({ theme }) => theme.dark};
`;

export const MetaLine = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.midGrey};
`;

export const Card = styled.section`
  border: 1px solid ${({ theme }) => theme.grey};
  border-radius: 0.875rem;
  padding: 1.25rem;
  background: ${({ theme }) => theme.background};

  & + & {
    margin-top: 1rem;
  }
`;

export const CardTitle = styled.h3`
  margin: 0 0 1rem;
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${({ theme }) => theme.dark};
`;

export const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 1.5rem;

  ${media.md} {
    grid-template-columns: 1fr;
  }
`;

export const DetailItem = styled.div<{ $span?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
  ${({ $span }) => ($span ? 'grid-column: 1 / -1;' : '')}
`;

export const DetailLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.midGrey};
`;

export const DetailValue = styled.div<{ $muted?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme, $muted }) => ($muted ? theme.midGrey : theme.dark)};
  word-break: break-word;
`;

export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

export const RiskChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.6rem;
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.negative90};
  color: ${({ theme }) => theme.negative};
  font-size: 0.75rem;
  font-weight: 600;
`;

/** Veriff holds the media, so the panel says so instead of showing a gap. */
export const MediaNotice = styled.p`
  margin: 0;
  padding: 0.875rem 1rem;
  border: 1px dashed ${({ theme }) => theme.grey};
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.midGrey};
`;

export const ReasonInput = styled.textarea`
  /* The global reset only sets border-box on body and pseudo-elements, so a
     100% width would otherwise overflow by this control's own padding. */
  box-sizing: border-box;
  width: 100%;
  min-height: 5.5rem;
  margin-top: 0.35rem;
  padding: 0.75rem 0.875rem;
  border: 1px solid ${({ theme }) => theme.grey};
  border-radius: 0.625rem;
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.dark};
  font: inherit;
  font-size: 0.875rem;
  resize: vertical;

  &:disabled {
    opacity: 0.65;
  }
`;

export const FieldError = styled.span`
  margin-top: 0.35rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.negative};
`;

export const CharCount = styled.span`
  margin-top: 0.35rem;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.midGrey};
`;
